import { useMemo, useRef, useState } from 'react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAction,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuItemLeadingAction,
  PrimaryNavigationMenuSearch,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import { FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS } from 'pages/Firms/common/constants';
import type { FirmSummaryFundFilterResult } from './useFirmSummaryFundFilter';

type Props = Omit<FirmSummaryFundFilterResult, 'titleAccessory'>;

interface FundFilterMenuOption {
  id: string;
  isAllFunds: boolean;
  label: string;
  value: string;
}

const ALL_FUNDS_OPTION_ID = 'all-funds';
const FUND_FILTER_MENU_WIDTH = 260;
const FIRM_SUMMARY_FUND_FILTER_TEST_IDS = {
  menu: 'firm-summary-fund-filter-menu',
  search: 'firm-summary-fund-filter-search',
  option: (optionId: string) => `firm-summary-fund-filter-option-${optionId}`,
};

const FirmSummaryFundFilterMenu = ({
  fundMenuAnchor,
  handleCloseFundMenu,
  fundOptions,
  isAllFundsSelected,
  selectedFundId,
  canCreateFund,
  canEditFund,
  handleSelectFund,
  handleAddFund,
  handleEditFund,
}: Props) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [hoveredOptionId, setHoveredOptionId] = useState<string | null>(null);
  const [editIconHoveredId, setEditIconHoveredId] = useState<string | null>(null);
  const menuOptions = useMemo<FundFilterMenuOption[]>(
    () => [
      {
        id: ALL_FUNDS_OPTION_ID,
        isAllFunds: true,
        label: FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
        value: FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
      },
      ...fundOptions.map(fund => ({
        id: String(fund.id),
        isAllFunds: false,
        label: fund.name,
        value: String(fund.id),
      })),
    ],
    [fundOptions]
  );
  const {
    filteredOptions,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    visibleOptions,
  } = useSearchableLimitedOptions({
    getSearchText: option => option.label,
    options: menuOptions,
  });

  const handleMenuClose = () => {
    resetSearchableOptions();
    handleCloseFundMenu();
  };

  const handleOptionSelect = (option: FundFilterMenuOption) => {
    resetSearchableOptions();
    handleSelectFund(option.value);
  };

  const handleAddFundClick = () => {
    resetSearchableOptions();
    handleAddFund();
  };

  return (
    <PrimaryNavigationMenu
      anchorEl={fundMenuAnchor}
      data-testid={FIRM_SUMMARY_FUND_FILTER_TEST_IDS.menu}
      disableAutoFocusItem
      open={Boolean(fundMenuAnchor)}
      onClose={handleMenuClose}
      menuMaxWidth={FUND_FILTER_MENU_WIDTH}
      menuMinWidth={FUND_FILTER_MENU_WIDTH}
      scrollHintItemLabelPlural="funds"
      scrollHintItemLabelSingular="fund"
      TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
      MenuListProps={{
        disableListWrap: true,
      }}>
      <PrimaryNavigationMenuSearch
        clearAriaLabel="Clear fund search"
        data-testid={FIRM_SUMMARY_FUND_FILTER_TEST_IDS.search}
        fullWidth
        inputRef={searchInputRef}
        onChange={event => setSearchValue(event.target.value)}
        onClear={() => setSearchValue('')}
        placeholder="Find a Fund"
        value={searchValue}
      />
      {visibleOptions.map(option => (
        <PrimaryNavigationMenuItem
          key={option.id}
          data-testid={FIRM_SUMMARY_FUND_FILTER_TEST_IDS.option(option.id)}
          isActive={option.isAllFunds ? isAllFundsSelected : selectedFundId === Number(option.id)}
          onClick={() => handleOptionSelect(option)}
          onMouseEnter={() => setHoveredOptionId(option.id)}
          onMouseLeave={() => setHoveredOptionId(null)}
          endIcon={option.isAllFunds || !canEditFund ? undefined : (
            <PrimaryNavigationMenuItemLeadingAction
              aria-label={`Edit ${option.label}`}
              isVisible={hoveredOptionId === option.id}
              style={{
                borderRadius: 6,
                width: 20,
                height: 20,
                padding: 2,
                backgroundColor: editIconHoveredId === option.id ? 'rgba(0,0,0,0.1)' : undefined,
                transition: 'background-color 120ms ease',
              }}
              onMouseEnter={() => setEditIconHoveredId(option.id)}
              onMouseLeave={() => setEditIconHoveredId(null)}
              onClick={e => {
                e.stopPropagation();
                handleEditFund(Number(option.id)).catch(() => undefined);
              }}>
              <EditOutlinedIcon fontSize="inherit" />
            </PrimaryNavigationMenuItemLeadingAction>
          )}>
          {option.label}
        </PrimaryNavigationMenuItem>
      ))}
      {filteredOptions.length === 0 && (
        <PrimaryNavigationMenuItem disabled tone="muted">
          No funds found
        </PrimaryNavigationMenuItem>
      )}
      {canCreateFund && (
        <PrimaryNavigationMenuAction icon={<AddOutlinedIcon fontSize="inherit" />} onClick={handleAddFundClick}>
          Add Fund
        </PrimaryNavigationMenuAction>
      )}
    </PrimaryNavigationMenu>
  );
};

export default FirmSummaryFundFilterMenu;
